import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const InfoItem = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" sx={{ color: 'var(--wb-muted-70)', textTransform: 'uppercase', letterSpacing: 0.6 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: 'var(--wb-text-title)', mt: 0.2, wordBreak: 'break-word' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

InfoItem.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.node])
};

const WorkspaceDetailDialog = ({ open, workspace, onClose }) => {
  if (!workspace) {
    return null;
  }

  const isSoftDeleted = workspace?.soft_delete?.deleted;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'var(--wb-bg-surface)',
          border: '1px solid var(--wb-border-16)'
        }
      }}
    >
      <DialogTitle sx={{ color: 'var(--wb-text-title)', pr: 6 }}>
        {workspace.name || 'Detalle del espacio de análisis'}
        <IconButton
          aria-label="Cerrar"
          onClick={onClose}
          sx={{ position: 'absolute', right: 16, top: 16, color: 'var(--wb-muted-80)' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ borderColor: 'var(--wb-border)' }}>
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          {workspace.is_active ? (
            <Chip size="small" label="Activo" sx={{ bgcolor: 'var(--wb-x-bg-4ade801f, rgba(74,222,128,0.12))', color: 'var(--wb-success)' }} />
          ) : (
            <Chip size="small" label="Inactivo" sx={{ bgcolor: 'var(--wb-border)', color: 'var(--wb-muted)' }} />
          )}
          {isSoftDeleted && (
            <Chip size="small" label="Eliminado (soft)" sx={{ bgcolor: 'var(--wb-x-bg-f871711f, rgba(248,113,113,0.12))', color: 'var(--wb-danger)' }} />
          )}
        </Stack>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <InfoItem label="Id Espacio de análisis" value={workspace.semantic_workspace_id} />
            <InfoItem label="Nombre" value={workspace.name} />
            <InfoItem label="Descripción" value={workspace.description || 'Sin descripción'} />
            <InfoItem label="Área de negocio" value={workspace.business_area || 'Sin definir'} />
            <InfoItem label="Cuenta" value={workspace.account_id || '—'} />
            <InfoItem label="Propietario" value={workspace.user_owner_id || '—'} />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoItem label="Versión" value={workspace.version || '—'} />
            <InfoItem
              label="Modelos vinculados"
              value={`${workspace.models_linked?.length || 0} modelos`}
            />
            <InfoItem
              label="Conversaciones"
              value={`${workspace.conversations?.length || 0} conversaciones`}
            />
            <InfoItem
              label="Usuarios permitidos"
              value={`${workspace.users_allowed?.length || 0} usuarios`}
            />
            <InfoItem
              label="Eliminación soft"
              value={
                isSoftDeleted
                  ? `Eliminado el ${workspace.soft_delete?.deleted_at || 'fecha no disponible'}`
                  : 'No eliminado'
              }
            />
          </Grid>
        </Grid>
        <Divider sx={{ my: 3, borderColor: 'var(--wb-x-border-94a3b814, rgba(148, 163, 184, 0.08))' }} />
        <Typography variant="subtitle2" sx={{ color: 'var(--wb-muted-80)', mb: 1 }}>
          Auditoría
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <InfoItem label="Creado por" value={workspace.audit?.created_by || '—'} />
            <InfoItem label="Creado el" value={workspace.audit?.created_at || '—'} />
          </Grid>
          <Grid item xs={12} md={6}>
            <InfoItem label="Actualizado por" value={workspace.audit?.updated_by || '—'} />
            <InfoItem label="Actualizado el" value={workspace.audit?.updated_at || '—'} />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid var(--wb-border)' }}>
        <Typography variant="body2" sx={{ color: 'var(--wb-muted-70)' }}>
          Espacios de análisis proporcionan contexto a los agentes LLM de WhisperBI.
        </Typography>
      </DialogActions>
    </Dialog>
  );
};

WorkspaceDetailDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  workspace: PropTypes.object,
  onClose: PropTypes.func.isRequired
};

WorkspaceDetailDialog.defaultProps = {
  workspace: null
};

export default WorkspaceDetailDialog;
